import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp } from 'lucide-react';

const ProfitTrendChart = ({ data, dateRange }) => {
  const chartData = useMemo(() => {
    if (!data || data?.length === 0) return [];

    // Group data by month or year based on dateRange
    const grouped = {};

    data?.forEach(calc => {
      const date = new Date(calc?.createdAt);
      let key;

      if (dateRange === 'year') {
        key = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
      } else {
        key = date?.toISOString()?.split('T')?.[0];
      }

      if (!grouped?.[key]) {
        grouped[key] = {
          period: key,
          revenue: 0,
          profit: 0,
          expenses: 0,
          count: 0
        };
      }

      grouped[key].revenue += calc?.totalObra || 0;
      grouped[key].expenses += calc?.totalGastos || 0;
      grouped[key].profit += calc?.lucroLiquido || 0;
      grouped[key].count += 1;
    });

    // Convert to array and sort by period
    return Object.values(grouped)?.sort((a, b) => a?.period?.localeCompare(b?.period))?.map(item => ({
        ...item,
        period: formatPeriod(item?.period, dateRange),
        margin: item?.revenue > 0 ? ((item?.profit / item?.revenue) * 100)?.toFixed(1) : 0
      }));
  }, [data, dateRange]);

  const formatPeriod = (period, range) => {
    if (range === 'year') {
      const [year, month] = period?.split('-');
      const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      return `${monthNames?.[parseInt(month) - 1]}/${year}`;
    }
    return new Date(period)?.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-4 shadow-lg">
          <p className="text-sm font-semibold text-foreground mb-2">{payload?.[0]?.payload?.period}</p>
          <div className="space-y-1">
            <p className="text-xs text-success">
              Receita: {formatCurrency(payload?.[0]?.payload?.revenue)}
            </p>
            <p className="text-xs text-primary">
              Lucro: {formatCurrency(payload?.[0]?.payload?.profit)}
            </p>
            <p className="text-xs text-destructive">
              Despesas: {formatCurrency(payload?.[0]?.payload?.expenses)}
            </p>
            <p className="text-xs text-muted-foreground">
              Margem: {payload?.[0]?.payload?.margin}%
            </p>
            <p className="text-xs text-muted-foreground">
              Projetos: {payload?.[0]?.payload?.count}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (chartData?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-5 w-5 text-primary" />
          <h3 className="text-lg sm:text-xl font-semibold text-foreground">Tendência de Lucro</h3>
        </div>
        <div className="text-center py-12 text-muted-foreground">
          <p>Nenhum dado disponível para o período selecionado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="h-5 w-5 text-primary" />
        <h3 className="text-lg sm:text-xl font-semibold text-foreground">Tendência de Lucro</h3>
      </div>
      <div className="w-full h-80 sm:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="period"
              tick={{ fill: '#6b7280', fontSize: 12 }}
              stroke="#9ca3af"
            />
            <YAxis
              tick={{ fill: '#6b7280', fontSize: 12 }}
              stroke="#9ca3af"
              tickFormatter={formatCurrency}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '12px' }}
              iconType="line"
            />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="#10b981"
              strokeWidth={2}
              name="Receita"
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="profit"
              stroke="#3b82f6"
              strokeWidth={2}
              name="Lucro"
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="expenses"
              stroke="#ef4444"
              strokeWidth={2}
              name="Despesas"
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ProfitTrendChart;